import {
    useState,
    useEffect
} from "react"
import loadUserProfile from "./Components/loadUser"
import { signOut } from "aws-amplify/auth"
import EmailCopy from "./Components/EmailCopy"
import Overlay from "../Overlay"

import { CheckCircle } from "lucide-react"

import {
    useQuery,
} from "@tanstack/react-query"

import icoverCredentialsStatusQuery from "./services/icover-credentials-status"
import UpdateIcoverCredentialsDialog from "./Components/updateIcoverCredentials"
import { isFinanceOnly } from "../../helpers/user-check-permissions"

export default function ProfileDialog() {
    const [user, setUser] = useState(null)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const [updatePasswordOpen, setUpdatePasswordOpen] = useState(false)
    const {
        data: icoverCredentialsStatusData,
    } = useQuery({
        queryKey: ['icover-credentials-status'],
        queryFn: () => icoverCredentialsStatusQuery(),
        enabled: user?.role === "ADMIN" && !isFinanceOnly(user?.scopes) && open,
    })

    useEffect(() => {
        loadUserProfile().then(setUser).catch(console.error)
    }, [])

    if (!user) return null

    // iCover (background-check vendor) credentials are for operational admin users — not finance.
    const showIcover = user.role === "ADMIN" && !isFinanceOnly(user.scopes)

    const fullName = `${user.givenName || ""} ${user.familyName || ""}`.trim()
    const initials = (fullName || user.email || "U")
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()

    const handleSignOut = async () => {
        try {
            setLoading(true)
            await signOut()
        } catch (err) {
            console.error("Error during sign out:", err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            <button
                onClick={() => setOpen(true)}
                className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100"
            >
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold text-sm">
                    {initials}
                </div>
            </button>

            {open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
                    <div className="bg-white rounded-2xl shadow-lg w-11/12 md:w-3/4 max-w-4xl h-auto md:h-3/4 flex flex-col md:flex-row overflow-hidden relative">
                        <button
                            onClick={() => setOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl"
                        >
                            ✕
                        </button>

                        {/* Left */}
                        <div className="md:w-1/3 w-full bg-gray-100 flex flex-col items-center justify-center p-6">
                            <div className="w-24 h-24 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-2xl">
                                {initials}
                            </div>
                            <p className="mt-4 text-lg font-semibold text-gray-700 text-center">
                                Welcome {fullName || "User"}
                            </p>
                        </div>

                        {/* Right */}
                        <div className="md:w-2/3 w-full p-6 flex flex-col overflow-y-auto max-h-[90vh]">
                            <div className="mt-10 flex-1 space-y-6">
                                {/* Full Name (only if exists) */}
                                {(user.givenName || user.familyName) && (
                                    <div className="grid grid-cols-2 gap-6">
                                        {user.givenName && (
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">
                                                    First Name
                                                </span>
                                                <span className="text-gray-700 font-semibold">
                                                    {user.givenName}
                                                </span>
                                            </div>
                                        )}
                                        {user.familyName && (
                                            <div>
                                                <span className="block text-sm font-medium text-gray-500">
                                                    Last Name
                                                </span>
                                                <span className="text-gray-700 font-semibold">
                                                    {user.familyName}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Email */}
                                <div>
                                    <span className="block text-sm font-medium text-gray-500">
                                        Email
                                    </span>
                                    <EmailCopy email={user.email} />
                                </div>

                                {/* Roles (only for ADMIN) */}
                                {
                                    user.role === "ADMIN" && (
                                        <div>
                                            <span className="block text-sm font-medium text-gray-500 mb-2">
                                                Roles Performed
                                            </span>
                                            <span className={`p-1 font-semibold uppercase rounded-md border-2 bg-white text-gray-700 border-border-focus`}>
                                                {(user.scopes || []).join(",") || "N/A"}
                                            </span>
                                        </div>
                                    )
                                }

                                {
                                    showIcover && icoverCredentialsStatusData?.credentialsSet && (
                                        <div className="flex flex-col gap-2 p-4 bg-green-50 border border-green-200 rounded-xl shadow-sm">
                                            {/* Success message */}
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="w-5 h-5 text-green-600" />
                                                <span className="text-sm font-medium text-green-800">
                                                    iCover credentials have been successfully set for this profile.
                                                </span>
                                            </div>

                                            {/* Username info */}
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <span className="font-medium text-gray-900">Username:</span>
                                                <span className="px-2 py-0.5 bg-white border border-gray-200 rounded-md shadow-sm">
                                                    {icoverCredentialsStatusData.username}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    showIcover && (
                                        <button
                                            type="button"
                                            onClick={() => setUpdatePasswordOpen(true)}
                                            className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm"
                                        >
                                            Update iCover Credentials
                                        </button>
                                    )}
                                <UpdateIcoverCredentialsDialog
                                    open={updatePasswordOpen}
                                    onClose={() => setUpdatePasswordOpen(false)}
                                />

                            </div>

                            <div className="mt-auto pt-6">
                                <button
                                    onClick={handleSignOut}
                                    disabled={loading}
                                    className="w-full bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Signing Out..." : "Sign Out"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Overlay />}
        </div>
    )
}
